<template>
  <div v-if="filters && filters.length > 0" class="dynamic-filters">
    <div v-for="filter in filters" :key="filter.name" class="filter-item">
      <label :for="`filter-${filter.name}`">{{ filter.label || filter.name }}</label>
      <div :ref="`${filter.name}DropdownRef`" class="multi-select-wrapper">
        <input
          :id="`filter-${filter.name}`"
          type="text"
          class="search-input-main"
          :placeholder="`Search or select ${filter.label || filter.name}...`"
          :value="openDropdowns[filter.name] ? (searchText[filter.name] || '') : getSelectedDisplayText(filter.name)"
          @input="updateSearch(filter.name, $event.target.value)"
          @click.stop="openDropdown(filter.name)"
        />
        <div v-if="openDropdowns[filter.name]" class="multi-select-dropdown">
          <div class="dropdown-items-container">
            <label v-for="option in getFilteredOptions(filter.name)" :key="option.value" class="checkbox-item">
              <input 
                type="checkbox" 
                :value="option.value" 
                :checked="isOptionSelected(filter.name, option.value)"
                @change="toggleOption(filter.name, option.value)"
              />
              <span>{{ option.key }}</span>
            </label>
            <div v-if="getFilteredOptions(filter.name).length === 0" class="no-results">
              No results found
            </div>
          </div>
          <div class="dropdown-actions">
            <button class="btn-clear" @click="clearFilter(filter.name)">
              Clear
            </button>
            <button class="btn-apply" @click="applyFilter(filter.name)">
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';

const props = defineProps({
  filters: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: Object,
    default: () => {},
  },
});

const emit = defineEmits(['update:modelValue']);

const openDropdowns = ref({});
const tempSelections = ref({});
const selectedFilters = ref({});
const searchText = ref({});

watch(() => props.modelValue, (newVal) => {
  selectedFilters.value = { ...newVal };
}, { deep: true });

watch(
  () => props.filters,
  (newFilters) => {
    // Initialize temp selections for each filter
    if (newFilters && newFilters.length > 0) {
      newFilters.forEach(filter => {
        if (!tempSelections.value[filter.name]) {
          tempSelections.value[filter.name] = [];
        }
        if (!selectedFilters.value[filter.name]) {
          selectedFilters.value[filter.name] = [];
        }
      });
    }
  },
  { deep: true, immediate: true }
);

const toggleDropdown = (filterName) => {
  if (!openDropdowns.value[filterName]) {
    // When opening, copy current selection to temp
    tempSelections.value[filterName] = [...(selectedFilters.value[filterName] || [])];
  } else {
    // When closing, clear search text
    searchText.value[filterName] = '';
  }
  openDropdowns.value[filterName] = !openDropdowns.value[filterName];
};

const openDropdown = (filterName) => {
  if (!openDropdowns.value[filterName]) {
    tempSelections.value[filterName] = [...(selectedFilters.value[filterName] || [])];
    openDropdowns.value[filterName] = true;
  }
};

const toggleOption = (filterName, optionValue) => {
  if (!tempSelections.value[filterName]) {
    tempSelections.value[filterName] = [];
  }
  const index = tempSelections.value[filterName].indexOf(optionValue);
  if (index > -1) {
    tempSelections.value[filterName].splice(index, 1);
  } else {
    tempSelections.value[filterName].push(optionValue);
  }
};

const isOptionSelected = (filterName, optionValue) => {
  return tempSelections.value[filterName]?.includes(optionValue) || false;
};

const getSelectedText = (filterName) => {
  const selected = selectedFilters.value[filterName];
  if (!selected || selected.length === 0) {
    return '';
  }
  return selected.join(', ');
};

const getSelectedDisplayText = (filterName) => {
  const selected = selectedFilters.value[filterName];
  if (!selected || selected.length === 0) {
    return '';
  }
  if (selected.length === 1) {
    return selected[0];
  }
  return `${selected[0]} +${selected.length - 1}`;
};

const applyFilter = (filterName) => {
  selectedFilters.value[filterName] = [...tempSelections.value[filterName]];
  openDropdowns.value[filterName] = false;
  emit('update:modelValue', { ...selectedFilters.value });
};

const clearFilter = (filterName) => {
  tempSelections.value[filterName] = [];
  selectedFilters.value[filterName] = [];
  openDropdowns.value[filterName] = false;
  emit('update:modelValue', { ...selectedFilters.value });
};

const updateSearch = (filterName, value) => {
  searchText.value[filterName] = value;
};

const getFilteredOptions = (filterName) => {
  const filter = props.filters.find(f => f.name === filterName);
  if (!filter) return [];
  
  const search = (searchText.value[filterName] || '').toLowerCase();
  if (!search) return filter.options;
  
  return filter.options.filter(option => 
    option.key.toLowerCase().includes(search) || 
    option.value.toLowerCase().includes(search)
  );
};

const handleClickOutside = (event) => {
  // Check if click is inside any .multi-select-wrapper element
  const isInsideFilter = event.target.closest('.multi-select-wrapper');
  
  // If click is outside any filter, close all dropdowns
  if (!isInsideFilter) {
    Object.keys(openDropdowns.value).forEach(filterName => {
      openDropdowns.value[filterName] = false;
    });
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style lang="scss" scoped>
.dynamic-filters {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
}

.filter-item {
  display: flex;
  align-items: center;
  gap: 8px;

  label {
    font-size: 12px;
    font-weight: 500;
    color: var(--body-text-color-secondary);
    white-space: nowrap;
  }
}

.multi-select-wrapper {
  position: relative;
  width: 200px;
}

.search-input-main {
  width: 100%;
  padding: 8px 12px;
  background: white;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  cursor: text;
  font-family: inherit;

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 3px rgba(var(--color-primary-rgb), 0.2);
  }

  &:hover {
    border-color: var(--gray-400);
  }

  &::placeholder {
    color: var(--body-text-color-secondary);
  }
}

.multi-select-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid var(--gray-300);
  border-top: none;
  border-radius: 0 0 4px 4px;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  max-height: 350px;

  .checkbox-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 12px;
    flex-shrink: 0;

    &:hover {
      background: var(--gray-100);
    }

    input {
      cursor: pointer;
    }

    span {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
}

.dropdown-items-container {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.no-results {
  padding: 16px 12px;
  text-align: center;
  font-size: 12px;
  color: var(--body-text-color-secondary);
}

.dropdown-actions {
  display: flex;
  gap: 8px;
  padding: 8px 12px;
  border-top: 1px solid var(--gray-200);
  background: var(--gray-50);
  flex-shrink: 0;
}

.btn-clear,
.btn-apply {
  flex: 1;
  padding: 6px;
  border: 1px solid var(--gray-300);
  background: white;
  border-radius: 3px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;

  &:hover {
    background: rgba(76, 175, 80, 0.8);
    color: white;
  }

  &:active {
    background: var(--gray-200);
  }
}

.btn-apply {
  background: var(--color-primary);
  color: white;
  border-color: var(--color-primary);

  &:hover {
    background: var(--gray-700);
  }
}
</style>
